/**
 * AI tools Usage API client + presentation helpers (Phase X.3.7 Slice B).
 *
 * Wraps GET /v1/admin-review/ai-usage (owner/admin-gated server-side via
 * requireCapability('manage_team'); the rail visibility is convenience, not
 * security). Mirrors src/lib/staffAdmin.ts.
 *
 * The route -> friendly-label lookup lives here and is the single place to
 * clean messy route cardinality (Forward-watch in the brief). `route` is the
 * per-handler AI_INVOCATIONS_ROUTE path constant, NOT the ai_drafts.tool_name
 * enum, so DraftsList's TOOL_LABEL is not reusable. Several routes may collapse
 * onto one tool; unknown / retired routes (e.g. the retired Phase-0
 * /api/claude entry point, still present on historical rows) fall into the
 * "Other" bucket via the default.
 */

import { apiErrorFrom } from './api';

export interface UsageGridCell {
  user_id: string;
  user_display_name: string | null;
  user_email: string | null;
  route: string;
  invocations: number;
  input_tokens: number;
  output_tokens: number;
}

export interface RecentInvocation {
  id: number;
  user_id: string;
  user_display_name: string | null;
  route: string;
  status: string;
  /** Why a failed call failed: the handler's error code and the first
   *  200 characters of its detail. Null on a successful call. */
  error_code: string | null;
  error_detail: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  occurred_at: string; // ISO timestamp on the wire
}

export interface UsageReport {
  window_days: number;
  grid: UsageGridCell[];
  feed: RecentInvocation[];
  /** The tenant's trailing-day token total, the figure its daily budget
   *  is checked against. */
  tenant_tokens_today: number;
  /** The daily token budget when one is set; null means no ceiling. */
  tenant_daily_token_budget: number | null;
  request_id?: string;
}

/** The time windows the Usage view offers, in days. Mirrors the repo. */
export const WINDOW_OPTIONS = [7, 30, 90] as const;
export type WindowDays = (typeof WINDOW_OPTIONS)[number];

/**
 * The fidelity editor's audit route: the second pass over each email draft,
 * on its own route so the counts stay the operator's own requests. Mirrors
 * EMAIL_FIDELITY_EDIT_ROUTE in api/_shared/aiTools/routeClasses.ts (a unit
 * test there pins the two strings together).
 */
export const EMAIL_FIDELITY_EDIT_ROUTE = '/api/ai-tools/email/fidelity-edit';

/**
 * Map an ai_invocations.route path constant to its user-facing tool label.
 * Every route in AI_INVOCATION_ROUTES (api/_shared/aiTools/routeClasses.ts,
 * the one definition) has a label here; a test reads that list and fails
 * on any route without one. Only a retired route (the Phase-0 /api/claude
 * entry point's historical rows) falls to "Other".
 */
const ROUTE_LABEL: Record<string, string> = {
  '/api/ai-tools/email/draft': 'Email Helper',
  '/api/ai-tools/email/refine': 'Email Helper: refine',
  // Its own label, so "Email Helper" keeps counting the fresh drafts
  // operators asked for, not the passes behind them.
  [EMAIL_FIDELITY_EDIT_ROUTE]: 'Email Helper: fidelity editor',
  '/api/ai-tools/assessment/draft': 'Formal Assessment Report',
  '/api/ai-tools/assessment/refine': 'Formal Assessment Report: refine',
  '/api/ai-tools/assessment-letter/draft': 'Summary Assessment Letter',
  '/api/ai-tools/brand-format/draft': 'Brand Formatter',
  '/api/ai-tools/med-list/draft': 'Medication List',
  // One route for a photographed list and an insurance card alike.
  '/api/ai-tools/photo-intake/extract': 'Photo intake and insurance card reader',
  '/api/notes/extraction': 'Case-note reader',
  '/api/notes/spice-extraction': 'SPICE note reader',
};

export function labelForRoute(route: string): string {
  return ROUTE_LABEL[route] ?? 'Other';
}

/**
 * Order-of-magnitude blended cost estimate, in USD, for a token total.
 * Deliberately coarse: one flat per-1k rate across every model and across the
 * input/output price split, always surfaced as an estimate and rounded for
 * display. Real per-model prices differ from this flat rate by up to roughly
 * an order of magnitude in either direction (an Opus-class model runs several
 * times higher per token than this figure; lighter models run lower), so the
 * dollar figure conveys scale, not spend. A per-model cost table is phase-2
 * forward-watch.
 */
const ROUGH_USD_PER_1K_TOKENS = 0.01;

export function estimateCostUsd(totalTokens: number): number {
  return (totalTokens / 1000) * ROUGH_USD_PER_1K_TOKENS;
}

const base = (slug: string): string =>
  `/api/t/${encodeURIComponent(slug)}/v1/admin-review/ai-usage`;

export const aiUsageApi = {
  async report(
    tenantSlug: string,
    windowDays: WindowDays,
    signal?: AbortSignal,
  ): Promise<UsageReport> {
    const r = await fetch(`${base(tenantSlug)}?days=${windowDays}`, {
      headers: { 'content-type': 'application/json' },
      signal,
    });
    if (!r.ok) throw await apiErrorFrom(r, `GET ${base(tenantSlug)}?days=${windowDays}`);
    return (await r.json()) as UsageReport;
  },
};
